import React from 'react';
import { MenuItem, Select as MUISelect, selectClasses, styled } from '@mui/material';
import { IconCaretDown } from 'assets/img';
import { SelectProps } from './select.types';

const Select = ({ initialOption, options, icon, onSelect }: SelectProps) => {
  const [current, setCurrent] = React.useState(initialOption);
  const Icon = icon
    ? React.cloneElement(icon, {
        ...icon.props,
        style: {
          ...icon.props.style,
          flexShrink: 0,
        },
      })
    : null;

  const handleChange: React.ComponentProps<typeof MUISelect>['onChange'] = (e) => {
    const { value } = e.target;

    const selected = options.find((option) => option.value === value);

    if (selected === undefined) {
      throw new Error(
        `Selected option was not found in a list of provided options, ${value} provided.`
      );
    }

    onSelect(selected);
    setCurrent(selected);
  };

  React.useEffect(() => {
    setCurrent(initialOption);
  }, [initialOption]);

  return (
    <StyledSelect
      IconComponent={IconCaretDown}
      value={current.value ?? ''}
      onChange={handleChange}
      displayEmpty
      renderValue={() => (
        <StyledMenuItemSelected>
          {Icon}
          <StyledOption>{current.label}</StyledOption>
        </StyledMenuItemSelected>
      )}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </StyledSelect>
  );
};

const StyledOption = styled('span')(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '120px',
}));

const StyledMenuItemSelected = styled(MenuItem)(() => ({
  display: 'flex',
  fontSize: '16px',
  fontWeight: 'bold',
  gap: '10px',
  '&:hover': {
    backgroundColor: 'unset',
  },
}));

const StyledSelect = styled(MUISelect)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingRight: theme.spacing(10),
  width: 'fit-content',

  '&:focus': {
    background: theme.palette.lwWhite,
    borderRadius: '16px',
  },

  [`& .${selectClasses.icon}`]: {
    right: theme.spacing(5),
    top: 'calc(50% - 12px)',
  },
}));

export { Select };
